import groq from "groq";
import { Page } from "../../wrappers/Page";
import { usePreviewSubscription } from "../../helpers/sanity/sanity";
import { getClient } from "../../helpers/sanity/server";
import { SchemaType } from "../../sanity/types";
import type { GetStaticPaths, GetStaticProps } from "next";
import { NavType } from "../../types";
import { navQuery } from "../../sections/Nav";
import React from "react";
import { StoryTemplate } from "../../templates/StoryTemplate";
import { ctaSectionGroq } from "../../sections/CtaSection.sanity";
import { referenceGroq } from "../../sections/Reference.sanity";
import { richTextGroq } from "../../sections/RichText.sanity";
import { FooterProps, footerQuery } from "../../sections/Footer";

const schemaType: SchemaType = "story";

/**
 * Sanity Query
 */

const pageQuery = groq`
  * {
    "page": *[_type == "${schemaType}" && slug.current == $slug]{
      title, 
      "slug": slug.current,
      "storyHero": {
        title,
        "category": "Verhaal",
        "tag": expertise->title,
        "image": mainImage,
        video
      },
      "ctaSection": ctaSection{
        ${ctaSectionGroq}
      },
      "related": {
        "title": "Meer verhalen",
        "items": related[]->{
          title,
          "url": "/verhalen/"+slug.current,
          "image": mainImage,
          "video": video,
          "subject": expertise->title
        }
      },
      "body": body[]{
        ${richTextGroq}
      },
      "references": references{
        ${referenceGroq}
      }[]
    }[0],
    ${footerQuery},
    ${navQuery},
  }[0]
`;

interface PageQueryType {
  page: {
    title?: string;
    slug?: string;
  };
  nav: NavType;
  footer: FooterProps | {};
}

/**
 * Page
 */

interface Props {
  data: {
    post: PageQueryType;
  };
  preview: boolean;
}

const SlugPage = ({
  data = { post: { page: {}, nav: [], footer: {} } },
  preview,
}: Props) => {
  const post = usePreviewSubscription(pageQuery, {
    params: { slug: data.post?.page?.slug },
    initialData: data.post,
    enabled: preview && Boolean(data.post?.page?.slug),
  }).data;

  if (!post?.page) {
    return <Page notFound={true} nav={post.nav} footer={post.footer} />;
  }

  const { title, slug } = post.page;

  return (
    <Page
      title={title}
      notFound={!slug}
      nav={post.nav}
      preview={preview}
      footer={post.footer}
    >
      <StoryTemplate page={post.page} />
    </Page>
  );
};

export default SlugPage;

/**
 * Static props
 */

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
}) => {
  const post: PageQueryType = await getClient(preview).fetch(pageQuery, {
    slug: params?.slug,
  });

  if (!post) return { props: { notFound: true, data: { post: {}, nav: [] } } };

  return {
    props: {
      preview,
      data: { post },
    },
    revalidate: 60,
  };
};

/**
 * Static paths
 */

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: string[] = await await getClient(false).fetch(
    `*[_type == "${schemaType}"] { "slug": slug.current }.slug`
  );

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: true,
  };
};
